import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Copy,
  Inbox,
  PhoneCall,
  Server,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Select } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { formatDateTime, truncate } from '@/lib/utils';

type Tab = 'inbound' | 'outbound-vm' | 'outbound-bigcall';

interface InboundEntry {
  id: string;
  vm_id: string | null;
  job_id: string | null;
  batch_id_hint: string | null;
  source_query: Record<string, unknown> | null;
  payload: Record<string, unknown> | null;
  payload_hash: string;
  received_at: string;
  processed_at: string | null;
  process_error: string | null;
}

interface OutboundVmEntry {
  id: string;
  source: string;
  external_batch_id: string | null;
  campaign_name: string | null;
  status: string | null;
  channels_enabled: string[];
  jobs_total: number | null;
  target_callback_url: string;
  raw_payload: Record<string, unknown> | null;
  created_at: string;
}

interface OutboundBigcallEntry {
  id: string;
  contact_id: string | null;
  state_id: string | null;
  target: string;
  target_url: string | null;
  status: string;
  payload: Record<string, unknown> | null;
  response_body: string | null;
  http_status: number | null;
  error: string | null;
  created_at: string;
}

// Loose typing on the lucide icon component shape — lucide-react's
// LucideProps spreads SVG attrs so a tighter signature collides at build.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconCmp = React.ComponentType<any>;

const TABS: { key: Tab; label: string; icon: IconCmp }[] = [
  { key: 'inbound', label: 'Reçu (VMs → Dispatcher)', icon: ArrowDownToLine },
  { key: 'outbound-vm', label: 'Envoyé aux VMs', icon: Server },
  { key: 'outbound-bigcall', label: 'Envoyé à BIG.call', icon: PhoneCall },
];

export default function WebhooksPage() {
  const [tab, setTab] = useState<Tab>('inbound');
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState('');
  const [openPayload, setOpenPayload] = useState<{ title: string; data: unknown } | null>(null);

  return (
    <div>
      <PageHeader
        title="Webhooks"
        description="Tout ce qui rentre et tout ce qu'on envoie — payloads bruts, copiables"
      />

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-brand-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
                active
                  ? 'border-brand-primary text-brand-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filtrer (id, payload, status…)"
          className="input flex-1 min-w-[240px]"
        />
        <span className="text-xs text-gray-400">Charger</span>
        <Select
          value={String(limit)}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="w-24"
        >
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="200">200</option>
          <option value="500">500</option>
        </Select>
      </div>

      {tab === 'inbound' && (
        <InboundList limit={limit} search={search} onOpen={setOpenPayload} />
      )}
      {tab === 'outbound-vm' && (
        <OutboundVmList limit={limit} search={search} onOpen={setOpenPayload} />
      )}
      {tab === 'outbound-bigcall' && (
        <OutboundBigcallList limit={limit} search={search} onOpen={setOpenPayload} />
      )}

      {openPayload && (
        <PayloadModal
          title={openPayload.title}
          data={openPayload.data}
          onClose={() => setOpenPayload(null)}
        />
      )}
    </div>
  );
}

interface ListProps {
  limit: number;
  search: string;
  onOpen: (p: { title: string; data: unknown }) => void;
}

function useFilter<T>(rows: T[] | undefined, search: string): T[] {
  return useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows ?? [];
    return (rows ?? []).filter((r) => JSON.stringify(r).toLowerCase().includes(s));
  }, [rows, search]);
}

function InboundList({ limit, search, onOpen }: ListProps) {
  const q = useQuery<InboundEntry[]>({
    queryKey: ['webhooks', 'inbound', limit],
    queryFn: () =>
      api
        .get<{ entries: InboundEntry[] }>('/v1/admin/webhooks/inbound', { limit })
        .then((r) => r.entries ?? []),
    refetchInterval: 10_000,
  });
  const filtered = useFilter(q.data, search);
  return (
    <Card>
      <CardHeader title={`${filtered.length} webhook(s) reçu(s)`} />
      <div className="overflow-x-auto">
        {q.isLoading && !q.data ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <Empty icon={Inbox} text="Aucun webhook reçu pour l'instant. Quand une VM POST sur /v1/results, ça apparaît ici." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-brand-border text-xs text-gray-500">
              <tr>
                <th className="th">Reçu</th>
                <th className="th">VM</th>
                <th className="th">Job</th>
                <th className="th">Batch (hint)</th>
                <th className="th">Hash</th>
                <th className="th">Traité</th>
                <th className="th">Erreur</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td className="td">{formatDateTime(e.received_at)}</td>
                  <td className="td font-mono text-xs">{truncate(e.vm_id ?? '—', 8)}</td>
                  <td className="td font-mono text-xs">{truncate(e.job_id ?? '—', 8)}</td>
                  <td className="td font-mono text-xs">{truncate(e.batch_id_hint ?? '—', 8)}</td>
                  <td className="td font-mono text-xs">{truncate(e.payload_hash, 10)}</td>
                  <td className="td">
                    {e.processed_at ? (
                      <span className="text-emerald-700">{formatDateTime(e.processed_at)}</span>
                    ) : (
                      <span className="text-amber-700">en attente</span>
                    )}
                  </td>
                  <td className="td text-xs text-red-600 max-w-[200px] truncate" title={e.process_error ?? ''}>
                    {e.process_error ?? '—'}
                  </td>
                  <td className="td">
                    <button
                      type="button"
                      onClick={() =>
                        onOpen({
                          title: `Inbound #${e.id} — reçu ${formatDateTime(e.received_at)}`,
                          data: { source_query: e.source_query, payload: e.payload },
                        })
                      }
                      className="btn-ghost text-xs"
                    >
                      Voir payload
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function OutboundVmList({ limit, search, onOpen }: ListProps) {
  const q = useQuery<OutboundVmEntry[]>({
    queryKey: ['webhooks', 'outbound-vm', limit],
    queryFn: () =>
      api
        .get<{ entries: OutboundVmEntry[] }>('/v1/admin/webhooks/outbound-vm', { limit })
        .then((r) => r.entries ?? []),
    refetchInterval: 10_000,
  });
  const filtered = useFilter(q.data, search);
  return (
    <Card>
      <CardHeader title={`${filtered.length} batch(es) reçu(s) en entrée — payload envoyé aux VMs`} />
      <div className="overflow-x-auto">
        {q.isLoading && !q.data ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <Empty
            icon={ArrowUpFromLine}
            text="Aucun batch encore. Quand bigcall ou getfishnet POST sur /v1/jobs, ça apparaît ici avec le payload qu'on relayera aux VMs."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-brand-border text-xs text-gray-500">
              <tr>
                <th className="th">Créé</th>
                <th className="th">Source</th>
                <th className="th">Batch externe</th>
                <th className="th">Campagne</th>
                <th className="th">Channels</th>
                <th className="th">Jobs</th>
                <th className="th">Statut</th>
                <th className="th">Callback URL</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td className="td">{formatDateTime(e.created_at)}</td>
                  <td className="td">{e.source}</td>
                  <td className="td font-mono text-xs">{truncate(e.external_batch_id ?? '—', 24)}</td>
                  <td className="td">{e.campaign_name ?? '—'}</td>
                  <td className="td text-xs">{(e.channels_enabled ?? []).join(', ') || '—'}</td>
                  <td className="td">{e.jobs_total ?? '—'}</td>
                  <td className="td text-xs">{e.status ?? '—'}</td>
                  <td className="td text-xs text-gray-500" title={e.target_callback_url}>
                    {truncate(e.target_callback_url, 36)}
                  </td>
                  <td className="td">
                    <button
                      type="button"
                      onClick={() =>
                        onOpen({
                          title: `Batch ${e.external_batch_id ?? e.id.slice(0, 8)} — payload reçu de ${e.source}`,
                          data: e.raw_payload,
                        })
                      }
                      className="btn-ghost text-xs"
                    >
                      Voir payload
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function OutboundBigcallList({ limit, search, onOpen }: ListProps) {
  const q = useQuery<OutboundBigcallEntry[]>({
    queryKey: ['webhooks', 'outbound-bigcall', limit],
    queryFn: () =>
      api
        .get<{ entries: OutboundBigcallEntry[] }>('/v1/admin/webhooks/outbound-bigcall', { limit })
        .then((r) => r.entries ?? []),
    refetchInterval: 10_000,
  });
  const filtered = useFilter(q.data, search);
  return (
    <Card>
      <CardHeader title={`${filtered.length} push(es) sortant(s)`} />
      <div className="overflow-x-auto">
        {q.isLoading && !q.data ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <Empty
            icon={PhoneCall}
            text="Aucun push sortant pour l'instant. Quand le pipeline pousse un contact à bigcall via /enrichment/ingest, ça apparaît ici avec la réponse HTTP."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-brand-border text-xs text-gray-500">
              <tr>
                <th className="th">Créé</th>
                <th className="th">Cible</th>
                <th className="th">Contact</th>
                <th className="th">Status</th>
                <th className="th">HTTP</th>
                <th className="th">URL</th>
                <th className="th">Erreur</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td className="td">{formatDateTime(e.created_at)}</td>
                  <td className="td">{e.target}</td>
                  <td className="td font-mono text-xs">{truncate(e.contact_id ?? '—', 8)}</td>
                  <td className="td text-xs">{e.status}</td>
                  <td className="td">
                    <span
                      className={`text-xs ${
                        (e.http_status ?? 0) >= 200 && (e.http_status ?? 0) < 300
                          ? 'text-emerald-700'
                          : 'text-red-700'
                      }`}
                    >
                      {e.http_status ?? '—'}
                    </span>
                  </td>
                  <td className="td text-xs text-gray-500" title={e.target_url ?? ''}>
                    {truncate(e.target_url ?? '—', 40)}
                  </td>
                  <td className="td text-xs text-red-600 max-w-[180px] truncate" title={e.error ?? ''}>
                    {e.error ?? '—'}
                  </td>
                  <td className="td">
                    <button
                      type="button"
                      onClick={() =>
                        onOpen({
                          title: `Push ${e.target} — ${e.http_status ?? '?'}`,
                          data: { request: e.payload, response_body: e.response_body, error: e.error },
                        })
                      }
                      className="btn-ghost text-xs"
                    >
                      Voir payload
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}

function SkeletonRows() {
  return (
    <div className="p-4 space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  );
}

function Empty({ icon: Icon, text }: { icon: IconCmp; text: string }) {
  return (
    <div className="p-10 text-center">
      <Icon size={28} className="mx-auto text-gray-300 mb-3" />
      <p className="text-sm text-gray-500 max-w-md mx-auto">{text}</p>
    </div>
  );
}

function PayloadModal({
  title,
  data,
  onClose,
}: {
  title: string;
  data: unknown;
  onClose: () => void;
}) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  function handleCopy() {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success('Payload copié'))
      .catch(() => toast.error('Échec copie'));
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
          <h2 className="font-heading font-bold text-brand-dark text-sm">{title}</h2>
          <div className="flex items-center gap-2">
            <button type="button" onClick={handleCopy} className="btn-ghost text-xs">
              <Copy size={12} /> Copier
            </button>
            <button type="button" onClick={onClose} className="btn-ghost text-xs">
              <X size={14} />
            </button>
          </div>
        </div>
        <pre className="overflow-auto flex-1 p-5 text-[12px] font-mono bg-gray-50 whitespace-pre-wrap break-all">
          {text}
        </pre>
      </div>
    </div>
  );
}
